import { BarLabelOverlay } from './BarLabelOverlay.js';
import { plotLayout } from '../../../../core/components/chart-grid/ChartGrid.sty.js';
import { CANVAS_DIMMED_OPACITY } from '../../../../core/utils/apply-opacity-to-canvas.js';
import { zIndicesXYChartAreas } from '../../../constants/xy-chart-z-index-areas.js';
import { useBarData } from '../../../hooks/useBarData.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import { useXYChartHighlightedSeriesIdsState } from '../../../store/selectors.js';
import { getHighlightedSeries } from '../../../utils/get-highlighted-series.js';

/**
 * Chart-level wrapper that mounts the HTML bar labels for every bar series
 * with `hasLabels === true`. Rendered as a sibling of the canvas plot inside
 * `<ChartGrid.PlotArea>` because HTML cannot live inside `<canvas>` — see
 * `./BarDisplay.tsx` for the canvas-paint counterpart.
 */
export function BarLabelLayout() {
  const axisIdToScales = useXYChartScales();
  const { filteredSeriesWithDatapoints, seriesToMetadata } = useBarData();
  const highlightedSeriesId = useXYChartHighlightedSeriesIdsState();

  const labelSeries = filteredSeriesWithDatapoints.filter(
    (s) => s.hasLabels === true,
  );

  if (labelSeries.length === 0) {
    return null;
  }

  const highlightedSeries = highlightedSeriesId
    ? getHighlightedSeries(labelSeries, highlightedSeriesId)
    : undefined;

  // z-index above the canvas (`plot` layer) so the HTML labels render on top
  // of the 0.8-opacity bar fills — otherwise the canvas paints over the
  // labels and blends them with the bar color.
  return (
    <div
      className={plotLayout}
      style={{
        zIndex: zIndicesXYChartAreas.plot + 1,
        pointerEvents: 'none',
      }}
    >
      {labelSeries.map((series) => {
        const metadata = seriesToMetadata.get(series);
        if (!metadata) {
          return null;
        }
        const opacity =
          highlightedSeries && series !== highlightedSeries
            ? +CANVAS_DIMMED_OPACITY
            : undefined;
        return (
          <BarLabelOverlay
            key={series.id}
            axisIdToScales={axisIdToScales}
            series={series}
            metadata={metadata}
            opacity={opacity}
          />
        );
      })}
    </div>
  );
}

BarLabelLayout['displayName'] = 'BarLabelLayout';
